import { useState } from 'react';
import { Resume } from '../../services/resume';
import { compareResumes, formatDifference } from '../../utils/resumeCompare';
import './ResumeCompare.css';

interface ResumeCompareProps {
  baseResume: Resume;
  comparedResume: Resume;
  onClose: () => void;
}

export function ResumeCompare({ baseResume, comparedResume, onClose }: ResumeCompareProps) {
  const [viewMode, setViewMode] = useState<'diff' | 'side-by-side'>('diff');
  
  const differences = compareResumes(baseResume, comparedResume);
  const hasDifferences = differences.length > 0;

  return (
    <div className="resumeCompareOverlay">
      <div className="resumeCompareModal">
        <div className="resumeCompareHeader">
          <h3 className="resumeCompareTitle">
            Compare Versions
          </h3>
          <div className="resumeCompareControls">
            <div className="resumeCompareViewModeSelector">
              <button
                className={`resumeCompareViewModeButton ${viewMode === 'diff' ? 'active' : ''}`}
                onClick={() => setViewMode('diff')}
              >
                Diff View
              </button>
              <button
                className={`resumeCompareViewModeButton ${viewMode === 'side-by-side' ? 'active' : ''}`}
                onClick={() => setViewMode('side-by-side')}
              >
                Side-by-Side
              </button>
            </div>
            <button className="resumeCompareCloseButton" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        <div className="resumeCompareContent">
          {!hasDifferences ? (
            <div className="resumeCompareNoChanges">
              <p>No differences between Version {baseResume.version} and Version {comparedResume.version}</p>
            </div>
          ) : viewMode === 'diff' ? (
            <div className="resumeCompareDiffView">
              <h4 className="resumeCompareSubtitle">Changes from Version {baseResume.version} to Version {comparedResume.version}</h4>
              <div className="resumeCompareDiffList">
                {differences.map((diff, index) => {
                  const formatted = formatDifference(diff);
                  return (
                    <div key={index} className="resumeCompareDiffItem">
                      <div className="resumeCompareDiffSection">
                        <h5>{formatted.label}</h5>
                        <div className="resumeCompareDiffChangeIndicator">
                          <span className="resumeCompareBadgeChanged">Modified</span>
                        </div>
                      </div>

                      <div className="resumeCompareDiffContent">
                        <div className="resumeCompareDiffBefore">
                          <p className="resumeCompareDiffLabel">Before</p>
                          <p className={`resumeCompareDiffText ${formatted.before ? 'hasContent' : 'empty'}`}>
                            {formatted.before || '(empty)'}
                          </p>
                        </div>

                        <div className="resumeCompareDiffArrow">→</div>

                        <div className="resumeCompareDiffAfter">
                          <p className="resumeCompareDiffLabel">After</p>
                          <p className={`resumeCompareDiffText ${formatted.after ? 'hasContent' : 'empty'}`}>
                            {formatted.after || '(empty)'}
                          </p>
                        </div>
                      </div>

                      <div className="resumeCompareImpactBar">
                        <div
                          className="resumeCompareImpactFill"
                          style={{
                            width: `${Math.max(0, Math.min(100, diff.relevanceImpact + 50))}%`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="resumeCompareSideBySideView">
              <div className="resumeCompareSideBySideColumn">
                <h4>Version {baseResume.version}</h4>
                <div className="resumeCompareSideBySideContent">
                  <div className="resumeCompareBlock">
                    <h5>Summary</h5>
                    <p>{baseResume.data.summary || 'No summary'}</p>
                  </div>

                  <div className="resumeCompareBlock">
                    <h5>Experience</h5>
                    <ul>
                      {(baseResume.data.experience || []).map((exp, idx) => (
                        <li key={idx}>{exp.position} @ {exp.company}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="resumeCompareBlock">
                    <h5>Skills</h5>
                    <ul>
                      {[
                        ...(baseResume.data.skills.technical || []),
                        ...(baseResume.data.skills.soft || []),
                      ].map((skill, idx) => (
                        <li key={idx}>{skill}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="resumeCompareDivider" />

              <div className="resumeCompareSideBySideColumn">
                <h4>Version {comparedResume.version}</h4>
                <div className="resumeCompareSideBySideContent">
                  <div className="resumeCompareBlock">
                    <h5>Summary</h5>
                    <p>{comparedResume.data.summary || 'No summary'}</p>
                  </div>

                  <div className="resumeCompareBlock">
                    <h5>Experience</h5>
                    <ul>
                      {(comparedResume.data.experience || []).map((exp, idx) => (
                        <li key={idx}>{exp.position} @ {exp.company}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="resumeCompareBlock">
                    <h5>Skills</h5>
                    <ul>
                      {[
                        ...(comparedResume.data.skills.technical || []),
                        ...(comparedResume.data.skills.soft || []),
                      ].map((skill, idx) => (
                        <li key={idx}>{skill}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
